import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  getGoalById,
  updateGoalRating,
  updateBehavioralRatings,
  completeFinalReview,
  isGlobalManagerReviewOpen,
  getCurrentQuarter
} from '../../services/goalsService';
import {
  isDemoGoalId,
  getDemoGoalById,
  updateDemoGoalGoals,
  completeDemoFinalReview
} from '../../services/demoData';
import { getAllManagers } from '../../services/userService';
import { GoalDocument, BehavioralRatings, Goal } from '../../types';
import {
  ArrowLeft,
  Save,
  AlertCircle,
  Users,
  Target,
  Calculator,
  FlaskConical
} from 'lucide-react';

type RatingOption = 'A' | 'B' | 'NI';
type BehavioralOption = 'A' | 'B' | 'NI';

const ratingOptions: { value: RatingOption; label: string; color: string }[] = [
  { value: 'A', label: 'At/Above Expectations', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'B', label: 'Below Expectations', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'NI', label: 'Needs Improvement', color: 'bg-red-100 text-red-700 border-red-300' },
];

const behavioralOptions: { value: BehavioralOption; label: string; color: string }[] = [
  { value: 'A', label: 'At/Above Expectations', color: 'bg-green-100 text-green-700' },
  { value: 'B', label: 'Below Expectations', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'NI', label: 'Needs Improvement', color: 'bg-red-100 text-red-700' },
];

const behavioralCategories = [
  { key: 'respectForOthers', label: 'Respect for Others' },
  { key: 'integrity', label: 'Integrity' },
  { key: 'teamwork', label: 'Teamwork & Collaboration' },
  { key: 'biasForAction', label: 'Bias for Action' },
  { key: 'carefulCommunication', label: 'Careful Communication' },
  { key: 'growthMindset', label: 'Growth Mindset' },
  { key: 'leadByExample', label: 'Lead by Example' },
] as const;

const FinalReview = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { user, isDeveloper } = useAuth();
  const { isDemoMode, refreshDemo } = useDemo();
  const navigate = useNavigate();
  const [goal, setGoal] = useState<GoalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [isManagerReviewOpen, setIsManagerReviewOpen] = useState(false);

  const [behavioralRatings, setBehavioralRatings] = useState<BehavioralRatings>({
    respectForOthers: null,
    integrity: null,
    teamwork: null,
    biasForAction: null,
    carefulCommunication: null,
    growthMindset: null,
    leadByExample: null,
  });

  // Check if this is a demo goal
  const isDemo = goalId ? isDemoGoalId(goalId) : false;

  // Check if user has canImpersonate permission
  useEffect(() => {
    const checkImpersonatePermission = async () => {
      if (!user?.email) {
        setPermissionLoading(false);
        return;
      }

      // Developers don't need the canImpersonate flag
      if (isDeveloper) {
        setPermissionLoading(false);
        return;
      }

      // In demo mode, always allow
      if (isDemoMode || isDemo) {
        setCanImpersonate(true);
        setPermissionLoading(false);
        return;
      }

      try {
        const managers = await getAllManagers();
        const currentUser = managers.find(m => m.email === user.email);
        if (currentUser?.canImpersonate) {
          setCanImpersonate(true);
        }
      } catch (error) {
        console.error('Error checking impersonate permission:', error);
      } finally {
        setPermissionLoading(false);
      }
    };

    checkImpersonatePermission();
  }, [user?.email, isDeveloper, isDemoMode, isDemo]);

  useEffect(() => {
    const fetchGoal = async () => {
      if (!goalId) return;
      try {
        let goalDoc: GoalDocument | null = null;

        if (isDemo) {
          goalDoc = getDemoGoalById(goalId);
        } else {
          goalDoc = await getGoalById(goalId);
        }

        setGoal(goalDoc);
        if (goalDoc) {
          setBehavioralRatings(goalDoc.behavioralRatings || {
            respectForOthers: null,
            integrity: null,
            teamwork: null,
            biasForAction: null,
            carefulCommunication: null,
            growthMindset: null,
            leadByExample: null,
          });

          // Demo mode - always open
          if (isDemo) {
            setIsManagerReviewOpen(true);
          } else {
            // Check if manager review is open for this quarter
            const { quarter, year } = getCurrentQuarter();
            const isCurrentQuarter = goalDoc.quarter === quarter && goalDoc.year === year;

            if (isCurrentQuarter) {
              // For current quarter, check global manager review setting
              const globalOpen = await isGlobalManagerReviewOpen(goalDoc.quarter, goalDoc.year);
              setIsManagerReviewOpen(globalOpen || isDeveloper);
            } else {
              // Past quarters are always open for review
              setIsManagerReviewOpen(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching goal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, [goalId, isDeveloper, isDemo]);

  const handleGoalRating = async (index: number, type: 'employee' | 'manager', rating: RatingOption) => {
    if (!goalId || !goal) return;

    try {
      if (isDemo) {
        // Demo mode - update in memory
        const updatedGoals: Goal[] = goal.goals.map((g, i) => {
          if (i === index) {
            return {
              ...g,
              [type === 'employee' ? 'employeeRating' : 'managerRating']: rating
            };
          }
          return g;
        });
        updateDemoGoalGoals(goalId, updatedGoals);
        const updated = getDemoGoalById(goalId);
        setGoal(updated);
        refreshDemo();
      } else {
        await updateGoalRating(goalId, index, type, rating);
        const updated = await getGoalById(goalId);
        setGoal(updated);
      }
    } catch (error) {
      console.error('Error updating rating:', error);
    }
  };

  const handleBehavioralRating = (key: keyof BehavioralRatings, value: BehavioralOption) => {
    setBehavioralRatings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveBehavioral = async () => {
    if (!goalId) return;

    try {
      if (!isDemo) {
        await updateBehavioralRatings(goalId, behavioralRatings);
        const updated = await getGoalById(goalId);
        setGoal(updated);
      }
      // Demo mode - behavioral ratings will be saved with complete
    } catch (error) {
      console.error('Error saving behavioral ratings:', error);
    }
  };

  const handleCompleteReview = async () => {
    if (!goalId || !goal) return;

    const allRated = goal.goals.every(g => g.managerRating !== null);
    if (!allRated) {
      setError('Please provide a manager rating for all goals.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Use manager's name from goal when impersonating
      const signerName = (canImpersonate && goal.managerEmail !== user?.email)
        ? (goal.managerName || goal.managerEmail)
        : (user?.displayName || 'Manager');

      if (isDemo) {
        // Demo mode - complete in memory
        completeDemoFinalReview(goalId, goal.goals, signerName, behavioralRatings);
        refreshDemo();
      } else {
        await handleSaveBehavioral();
        await completeFinalReview(goalId, signerName);
      }
      navigate('/manager');
    } catch (error) {
      console.error('Error completing review:', error);
      setError('Failed to complete review. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isManager = isDemo || isDemoMode || user?.email === goal?.managerEmail || isDeveloper || canImpersonate;
  const isEmployee = user?.uid === goal?.userId;

  if (loading || permissionLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cps-primary"></div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Goal not found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!isManagerReviewOpen && !isDeveloper && !isDemo) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Manager Review Not Yet Open</h2>
          <p className="text-gray-600 mb-4">
            The manager review period for Q{goal.quarter} {goal.year} has not started yet.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Demo indicator */}
      {isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
          <FlaskConical className="w-5 h-5" />
          <span className="font-medium">Demo Goal</span>
          <span className="text-sm">- Changes are for training purposes only</span>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">End of Quarter Review</h1>
          <p className="text-gray-500">{goal.userName} • Q{goal.quarter} {goal.year}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Goal Ratings</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            A = At or above expectations • B = Below expectations • NI = Needs Improvement
          </p>
        </div>

        <div className="divide-y">
          {goal.goals.map((g, index) => (
            <div key={index} className="p-4">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-gray-900">
                    {index + 1}. {g.title}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    g.type === 'normal' ? 'bg-blue-100 text-blue-700' :
                    g.type === 'stretch' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {g.type}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{g.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Employee Rating</p>
                  <div className="flex gap-2">
                    {ratingOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => isEmployee && handleGoalRating(index, 'employee', option.value)}
                        disabled={!isEmployee}
                        className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                          g.employeeRating === option.value
                            ? option.color + ' border-current'
                            : 'bg-gray-50 text-gray-400 border-transparent hover:bg-gray-100'
                        } ${!isEmployee ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        {option.value}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Manager Rating</p>
                  <div className="flex gap-2">
                    {ratingOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => isManager && handleGoalRating(index, 'manager', option.value)}
                        disabled={!isManager}
                        className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                          g.managerRating === option.value
                            ? option.color + ' border-current'
                            : 'bg-gray-50 text-gray-400 border-transparent hover:bg-gray-100'
                        } ${!isManager ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        {option.value}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Goal Score Calculation */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Goal Performance Score</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            A = 3 pts • B = 2 pts • NI = 1 pt • Max = 15 pts
          </p>
        </div>

        <div className="p-4">
          <div className="space-y-3 mb-4">
            {goal.goals.map((g, index) => {
              const empScore = g.employeeRating === 'A' ? 3 : g.employeeRating === 'B' ? 2 : g.employeeRating === 'NI' ? 1 : 0;
              const mgrScore = g.managerRating === 'A' ? 3 : g.managerRating === 'B' ? 2 : g.managerRating === 'NI' ? 1 : 0;

              return (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700 truncate flex-1">
                    {index + 1}. {g.title || 'Untitled Goal'}
                  </span>
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-center min-w-[60px]">
                      <p className="text-xs text-gray-500">Employee</p>
                      <p className={`font-semibold ${empScore > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                        {empScore > 0 ? `${empScore} pts` : '-'}
                      </p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      <p className="text-xs text-gray-500">Manager</p>
                      <p className={`font-semibold ${mgrScore > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                        {mgrScore > 0 ? `${mgrScore} pts` : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {(() => {
            const employeeTotal = goal.goals.reduce((sum, g) => {
              return sum + (g.employeeRating === 'A' ? 3 : g.employeeRating === 'B' ? 2 : g.employeeRating === 'NI' ? 1 : 0);
            }, 0);
            const managerTotal = goal.goals.reduce((sum, g) => {
              return sum + (g.managerRating === 'A' ? 3 : g.managerRating === 'B' ? 2 : g.managerRating === 'NI' ? 1 : 0);
            }, 0);
            const employeePercent = ((employeeTotal / 15) * 100).toFixed(2);
            const managerPercent = ((managerTotal / 15) * 100).toFixed(2);

            return (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-sm text-blue-600 mb-1">Employee Score</p>
                  <p className="text-2xl font-bold text-blue-700">{employeeTotal}/15</p>
                  <p className="text-lg font-semibold text-blue-600">{employeePercent}%</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-sm text-green-600 mb-1">Manager Score</p>
                  <p className="text-2xl font-bold text-green-700">{managerTotal}/15</p>
                  <p className="text-lg font-semibold text-green-600">{managerPercent}%</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {isManager && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="p-4 border-b">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Behavioral Performance</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              A = At/Above Expectations • B = Below Expectations • NI = Needs Improvement
            </p>
          </div>

          <div className="p-4">
            <div className="space-y-3">
              {behavioralCategories.map(category => (
                <div key={category.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">{category.label}</span>
                  <div className="flex gap-2">
                    {behavioralOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleBehavioralRating(category.key, option.value)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          behavioralRatings[category.key] === option.value
                            ? option.color
                            : 'bg-white border border-gray-200 text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {option.value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {isManager ? 'Save & Exit' : 'Back'}
        </button>
        {isManager && (
          <button
            onClick={handleCompleteReview}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Completing...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Complete Final Review
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default FinalReview;